/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { BaseService } from 'apps/console-core/src/Services/_base';
import { HttpResponse } from 'apps/console-core/src/Services/http';
import { Observable, of, switchMap } from 'rxjs';
import {
  EntityEventSubscription,
  EntityEventSubscriptionRule,
  EntityEventSubscriptionRuleCreated,
  EntityEventSubscriptionRuleUpdated,
  EntityEventSubscriptionService,
  EntityEventSubscriptionTargetCreated,
  EntityEventSubscriptionTargetUpdated,
} from './Entities';
import { EntityEventSchedule } from './Entities/EventSchedule/EventSchedule.entity';
import { EntityEventScheduleCreated } from './Entities/EventScheduleCreated/EventScheduleCreated.entity';
import { EventBusServiceConfiguration } from './EventBus.service.config';
import {
  EventBusCreateEventSubscriptionTargetDto,
  EventBusCreateScheduleDto,
  EventBusCreateSubscriptionRuleDto,
  EventBusDeleteEventSubscriptionTargetDto,
  EventBusDeleteScheduleDto,
  EventBusDeleteSubscriptionRuleDto,
  EventBusGetEventSubscriptionsDto,
  EventBusGetScheduleDto,
  EventBusGetSubscriptionRuleDto,
  EventBusUpdateEventSubscriptionTargetDto,
  EventBusUpdateSubscriptionRuleDto,
} from './EventBus.service.dto';

/**
 * The event bus service.
 * @export
 * @class EventBusService
 */
export class EventBusService extends BaseService {
  /**
   * Creates an instance of EventBusService.
   * @param {EventBusServiceConfiguration} configuration The configuration options to accept.
   * @memberof EventBusService
   */
  public constructor(configuration: EventBusServiceConfiguration) {
    super(configuration);
  }

  /**
   * Creates base api url for the Event Bus service.
   * @returns {string | null} The base api url for the Event Bus service.
   * @memberof EventBusService
   */
  getBaseServiceUrl(): string | null {
    // This service is deployed in multiple regions and
    // needs to use the tenant's region.
    return this.getServiceUrlByTenant(`event-bus`);
  }

  /**
   * Creates api url for event subscriptions.
   * @param {string} [subscriptionId] The ID of the event subscription.
   * @returns {string | null} The api url for the subscription.
   * @memberof EventBusService
   */
  public createEventSubscriptionsUrl(subscriptionId?: string): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return subscriptionId ? `${baseUrl}/subscriptions/${subscriptionId}` : `${baseUrl}/subscriptions`;
  }

  /**
   * Creates api url for event subscription targets.
   * @param {string} subscriptionId The ID of the event subscription.
   * @param {string} [targetName] The name of the event subscription target.
   * @returns {string | null} The api url for the subscription target.
   * @memberof EventBusService
   */
  public createEventSubscriptionTargetsUrl(subscriptionId: string, targetName?: string): string | null {
    const baseSubscriptionUrl = this.createEventSubscriptionsUrl(subscriptionId);

    if (!baseSubscriptionUrl) {
      return null;
    }

    return targetName ? `${baseSubscriptionUrl}/targets/${targetName}` : `${baseSubscriptionUrl}/targets`;
  }

  /**
   * Creates api url for event schedules.
   */
  public createEventScheduleUrl(scheduleName?: string): string | null {
    const baseUrl = this.getBaseServiceUrl();

    if (!baseUrl) {
      return null;
    }

    return scheduleName ? `${baseUrl}/schedules/${scheduleName}` : `${baseUrl}/schedules`;
  }

  /**
   * Attempt to get a list of event subscriptions.
   * @param {EventBusGetEventSubscriptionsDto} dto The DTO to use for retrieving a list of event subscriptions.
   * @returns {Observable<Array<EntityEventSubscription> | null>} The observable of event subscriptions.
   * @memberof EventBusService
   */
  public getEventSubscriptions(
    dto: EventBusGetEventSubscriptionsDto,
  ): Observable<Array<EntityEventSubscription> | null> {
    const { service, tenantId } = dto;

    // Required `tenantID` query parameter for authorisation.
    const queryParams: Array<string> = [`${encodeURIComponent('tenantID')}=${tenantId}`];

    if (service) {
      queryParams.push(`${encodeURIComponent('service')}=${service}`);
    }

    const baseSubscriptionsUrl = this.createEventSubscriptionsUrl();
    if (!baseSubscriptionsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseSubscriptionsUrl);

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<Array<EntityEventSubscription>> => {
          return this._handleResponseError<Array<EntityEventSubscription>>(value);
        }),
      );
  }

  /**
   * Attempt to get an event subscription rule.
   * @param {EventBusGetSubscriptionRuleDto} dto The DTO to use for retrieving an event subscription rule.
   * @returns {Observable<EntityEventSubscriptionRule | null>} The observable of the event subscription rule.
   * @memberof EventBusService
   */
  public getEventSubscriptionRule(dto: EventBusGetSubscriptionRuleDto): Observable<EntityEventSubscriptionRule | null> {
    const { subscriptionId, tenantId } = dto;

    // Required `tenantID` query parameter for authorisation.
    const queryParams: Array<string> = [`${encodeURIComponent('tenantID')}=${tenantId}`];

    const baseSubscriptionsUrl = this.createEventSubscriptionsUrl(subscriptionId);
    if (!baseSubscriptionsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseSubscriptionsUrl);

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityEventSubscriptionRule> => {
          return this._handleResponseError<EntityEventSubscriptionRule>(value);
        }),
      );
  }

  /**
   * Attempt to update an event subscription rule.
   * @param {EventBusUpdateSubscriptionRuleDto} dto The DTO to use for updating an event subscription rule.
   * @returns {Observable<EntityEventSubscriptionRuleUpdated | null>} The observable of the event subscription rule.
   * @memberof EventBusService
   */
  public updateEventSubscriptionRule(
    dto: EventBusUpdateSubscriptionRuleDto,
  ): Observable<EntityEventSubscriptionRuleUpdated | null> {
    const { name, pattern, subscriptionId, tenantId } = dto;

    // Required `tenantID` query parameter for authorisation.
    const queryParams: Array<string> = [`${encodeURIComponent('tenantID')}=${tenantId}`];

    const baseSubscriptionsUrl = this.createEventSubscriptionsUrl(subscriptionId);
    if (!baseSubscriptionsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseSubscriptionsUrl);

    return this.httpService
      .patch<unknown, HttpResponse>(requestUrl, {
        body: {
          name,
          pattern,
          subscriptionId,
        },
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityEventSubscriptionRuleUpdated> => {
          return this._handleResponseError<EntityEventSubscriptionRuleUpdated>(value);
        }),
      );
  }

  /**
   * Attempt to delete an event subscription rule.
   * @param {EventBusDeleteSubscriptionRuleDto} dto The DTO to use for deleting the event subscription rule.
   * @returns {Observable<null>} The observable of a successful 204 response.
   * @memberof EventBusService
   */
  public deleteEventSubscriptionRule(dto: EventBusDeleteSubscriptionRuleDto): Observable<null> {
    const { subscriptionId, tenantId } = dto;

    // Required `tenantID` query parameter for authorisation.
    const queryParams: Array<string> = [`${encodeURIComponent('tenantID')}=${tenantId}`];

    const baseSubscriptionsUrl = this.createEventSubscriptionsUrl(subscriptionId);
    if (!baseSubscriptionsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseSubscriptionsUrl);

    return this.httpService
      .delete<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<null> => {
          return this._handleResponseError<null>(value);
        }),
      );
  }

  /**
   * Attempt to creating a new event subscription rule.
   * @param {EventBusCreateSubscriptionRuleDto} dto The DTO to use for creating an event subscription rule.
   * @returns {Observable<EntityEventSubscriptionRuleCreated | null>} The observable of the creation response.
   * @memberof EventBusService
   */
  public createEventSubscriptionRule(
    dto: EventBusCreateSubscriptionRuleDto,
  ): Observable<EntityEventSubscriptionRuleCreated | null> {
    const { name, pattern, tenantId } = dto;

    // Required `tenantID` query parameter for authorisation.
    const queryParams: Array<string> = [`${encodeURIComponent('tenantID')}=${tenantId}`];

    const baseSubscriptionsUrl = this.createEventSubscriptionsUrl();
    if (!baseSubscriptionsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseSubscriptionsUrl);

    return this.httpService
      .post<unknown, HttpResponse>(requestUrl, {
        body: {
          name,
          pattern,
          service: EntityEventSubscriptionService.dxpConsole,
          tenantID: tenantId,
        },
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityEventSubscriptionRuleCreated> => {
          return this._handleResponseError<EntityEventSubscriptionRuleCreated>(value);
        }),
      );
  }

  /**
   * Attempt to add a subscription target to send events to.
   * @param {EventBusCreateEventSubscriptionTargetDto} dto The DTO to use for creating an event subscription target.
   * @returns {Observable<EntityEventSubscriptionTargetCreated | null>} The observable of the creation response.
   * @memberof EventBusService
   */
  public createEventSubscriptionTarget(
    dto: EventBusCreateEventSubscriptionTargetDto,
  ): Observable<EntityEventSubscriptionTargetCreated | null> {
    const { name, details, inputTransformer, subscriptionId, tenantId, type } = dto;

    // Required `tenantID` query parameter for authorisation.
    const queryParams: Array<string> = [`${encodeURIComponent('tenantID')}=${tenantId}`];

    const baseTargetsUrl = this.createEventSubscriptionTargetsUrl(subscriptionId);
    if (!baseTargetsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseTargetsUrl);

    return this.httpService
      .post<unknown, HttpResponse>(requestUrl, {
        body: {
          details,
          inputTransformer,
          name,
          type,
        },
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityEventSubscriptionTargetCreated> => {
          return this._handleResponseError<EntityEventSubscriptionTargetCreated>(value);
        }),
      );
  }

  /**
   * Attempt to update an event subscription target.
   * @param {EventBusUpdateEventSubscriptionTargetDto} dto The DTO to use for updating an event subscription target.
   * @returns {Observable<EntityEventSubscriptionTargetUpdated | null>} The observable of the update response.
   * @memberof EventBusService
   */
  public updateEventSubscriptionTarget(
    dto: EventBusUpdateEventSubscriptionTargetDto,
  ): Observable<EntityEventSubscriptionTargetUpdated | null> {
    const { name, details, inputTransformer, subscriptionId, tenantId, type } = dto;

    // Required `tenantID` query parameter for authorisation.
    const queryParams: Array<string> = [`${encodeURIComponent('tenantID')}=${tenantId}`];

    const baseTargetsUrl = this.createEventSubscriptionTargetsUrl(subscriptionId, name);
    if (!baseTargetsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseTargetsUrl);

    return this.httpService
      .patch<unknown, HttpResponse>(requestUrl, {
        body: {
          details,
          inputTransformer,
          type,
        },
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityEventSubscriptionTargetUpdated> => {
          return this._handleResponseError<EntityEventSubscriptionTargetUpdated>(value);
        }),
      );
  }

  /**
   * Attempt to delete an event subscription target.
   * @param {EventBusDeleteEventSubscriptionTargetDto} dto The DTO to use for deleting an event subscription target.
   * @returns {Observable<null>} The observable of a successful 204 response.
   * @memberof EventBusService
   */
  public deleteEventSubscriptionTarget(dto: EventBusDeleteEventSubscriptionTargetDto): Observable<null> {
    const { subscriptionId, targetName, tenantId } = dto;

    // Required `tenantID` query parameter for authorisation.
    const queryParams: Array<string> = [`${encodeURIComponent('tenantID')}=${tenantId}`];

    const baseTargetsUrl = this.createEventSubscriptionTargetsUrl(subscriptionId, targetName);
    if (!baseTargetsUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    const requestUrl = this.appendQueryParamsToUrl(queryParams, baseTargetsUrl);

    return this.httpService
      .delete<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<null> => {
          return this._handleResponseError<null>(value);
        }),
      );
  }

  /**
   * Attempt to add a schedule for when an event is posted to the event bus.
   */
  public createEventSchedule(dto: EventBusCreateScheduleDto): Observable<EntityEventScheduleCreated | null> {
    const requestUrl = this.createEventScheduleUrl();
    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .post<unknown, HttpResponse>(requestUrl, {
        body: { ...dto },
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityEventScheduleCreated> => {
          return this._handleResponseError<EntityEventScheduleCreated>(value);
        }),
      );
  }

  /**
   * Attempt to retrieve a specific event schedule.
   */
  public getEventSchedule(dto: EventBusGetScheduleDto): Observable<EntityEventSchedule | null> {
    const { scheduleName } = dto;

    const requestUrl = this.createEventScheduleUrl(scheduleName);
    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .get<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<EntityEventSchedule> => {
          return this._handleResponseError<EntityEventSchedule>(value);
        }),
      );
  }

  /**
   * Attempt to delete an event schedule.
   */
  public deleteEventSchedule(dto: EventBusDeleteScheduleDto): Observable<null> {
    const { scheduleName } = dto;

    const requestUrl = this.createEventScheduleUrl(scheduleName);
    if (!requestUrl) {
      // eslint-disable-next-line no-console
      console.error(`Undefined tenant`);
      return of(null);
    }

    return this.httpService
      .delete<unknown, HttpResponse>(requestUrl, {
        ...this.getSessionRequestOptions(),
      })
      .pipe(
        switchMap((value: HttpResponse): Observable<null> => {
          return this._handleResponseError<null>(value);
        }),
      );
  }
}
